import {
	DollarOutlined,
	DownCircleOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
	FileTextOutlined,
} from '@ant-design/icons';
import {
	Button,
	Dropdown,
	Menu,
	Modal,
	Space,
	Table,
	Tag,
	Tooltip,
} from 'antd';
import { PermissionContext } from 'components/Global/AuthLayout';
import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	ApprovedByAccounts,
	DeleteActionSvg,
	EditActionSvg,
	LoanProcessing,
	Paid,
	PendingForApproval,
	RejectedByAccounts,
} from 'utils/svgs';
import { formatDate, formatNumber } from 'utils/utils';
import styles from './index.module.scss';
import { useNavigate } from 'react-router-dom';
import LogsModal from 'components/LoanRequest/LogsModal';
import './index.scss';

const { Column } = Table;

type Props = {
	tableData: any;
	totalRecords: number;
	currentPage: number;
	pageSize: number;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	isLoading: boolean;
	updateLoanRequestStatus: (id: string, status: string) => void;
	showDeleteModal: () => void;
	setDeleteLoan: any;
	confirmLoanModal: () => void;
	setUpdateEffectiveDate: any;
	handleEdit: any;
	handleModalOpen: (id: string) => void;
	// NEW PROPS for checkbox
	selectedLoanKeys: string[];
	onSelectChange: (selectedRowKeys: string[]) => void;
	selectedCurrency: 'ZMW' | 'USD';
	setIsBankErrorModalVisible: any;
	isBankErrorModalVisible: boolean;
};

const LoanRequestTable = (props: Props) => {
	const permissionContext = useContext(PermissionContext);
	const isEdit =
		permissionContext.allowedPermissions.includes('Edit_Loan_Request');
	const isDelete = permissionContext.allowedPermissions.includes(
		'Delete_Loan_Request'
	);

	const { data: profileData } = useSelector(
		(state: any) => state?.userProfile
	);

	const {
		tableData,
		showDeleteModal,
		totalRecords,
		currentPage,
		pageSize,
		tableChangeHandler,
		setDeleteLoan,
		isLoading,
		updateLoanRequestStatus,
		confirmLoanModal,
		handleEdit,
		setUpdateEffectiveDate,
		handleModalOpen,
		selectedLoanKeys,
		onSelectChange,
		selectedCurrency,
		isBankErrorModalVisible,
		setIsBankErrorModalVisible,
	} = props;

	const [isLogsModalVisible, setIsLogsModalVisible] = useState(false);
	const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);

	const navigate = useNavigate();

	const handleMenuClick = (record: any, e: any) => {
		if (record.status === 'LOAN_CREDITED') {
			return;
		} else if (record.status === e.key) {
			return;
		} else if (e.key === 'LOAN_CREDITED') {
			setUpdateEffectiveDate(record);
			confirmLoanModal();
		} else {
			updateLoanRequestStatus(record.id, e.key);
		}
	};

	const { data } = useSelector((state: any) => state?.userProfile);

	const statusMenu = (record: any) => (
		<Menu onClick={(e: any) => handleMenuClick(record, e)}>
			<Menu.Item
				key="PENDING_FOR_APPROVAL"
				className={styles['menuItem']}
				disabled={isOptionDisabled(
					record.status,
					'PENDING_FOR_APPROVAL'
				)}
			>
				<span className={styles['menuIcon']}>
					<PendingForApproval />
				</span>
				Pending for approval
			</Menu.Item>
			<Menu.Item
				key="APPROVED_BY_ACCOUNTS"
				className={styles['menuItem']}
				disabled={isOptionDisabled(
					record.status,
					'APPROVED_BY_ACCOUNTS'
				)}
			>
				<span className={styles['menuIcon']}>
					<ApprovedByAccounts />
				</span>
				Approved by accounts
			</Menu.Item>
			<Menu.Item
				key="REJECTED_BY_ACCOUNTS"
				className={styles['menuItem']}
				disabled={isOptionDisabled(
					record.status,
					'REJECTED_BY_ACCOUNTS'
				)}
			>
				<span className={styles['menuIcon']}>
					<RejectedByAccounts />
				</span>
				Rejected by accounts
			</Menu.Item>
			<Menu.Item
				key="LOAN_PROCESSING"
				className={styles['menuItem']}
				disabled={isOptionDisabled(record.status, 'LOAN_PROCESSING')}
			>
				<span className={styles['menuIcon']}>
					<LoanProcessing />
				</span>
				Loan processing
			</Menu.Item>
			<Menu.Item
				key="LOAN_CREDITED"
				className={styles['menuItem']}
				disabled={isOptionDisabled(record.status, 'LOAN_CREDITED')}
			>
				<span className={styles['menuIcon']}>
					<Paid />
				</span>
				Loan credited
			</Menu.Item>
		</Menu>
	);

	const disabledOptionsByStatus: { [key: string]: string[] } = {
		REJECTED_BY_ACCOUNTS: [
			'APPROVED_BY_ACCOUNTS',
			'PENDING_FOR_APPROVAL',
			'LOAN_CREDITED',
			'LOAN_PROCESSING',
		],
		LOAN_CREDITED: [
			'APPROVED_BY_ACCOUNTS',
			'REJECTED_BY_ACCOUNTS',
			'PENDING_FOR_APPROVAL',
			'LOAN_PROCESSING',
		],
		APPROVED_BY_ACCOUNTS: [
			'PENDING_FOR_APPROVAL',
			'REJECTED_BY_ACCOUNTS',
			'PENDING_FOR_APPROVAL',
		],
		LOAN_PROCESSING: [
			'REJECTED_BY_ACCOUNTS',
			'APPROVED_BY_ACCOUNTS',
			'PENDING_FOR_APPROVAL',
		],
		PENDING_FOR_APPROVAL: [
			'APPROVED_BY_ACCOUNTS',
			'REJECTED_BY_ACCOUNTS',
			'LOAN_CREDITED',
			'LOAN_PROCESSING',
		],
	};

	const isOptionDisabled = (currentStatus: string, option: string) => {
		return disabledOptionsByStatus[currentStatus]?.includes(option);
	};

	const deleteDataHandler = (id: string) => {
		setDeleteLoan(id);
		showDeleteModal();
	};

	const editDataHandler = (loanObject: any) => {
		handleEdit(loanObject);
	};

	const rowSelection = useMemo(() => {
		return {
			selectedRowKeys: selectedLoanKeys,

			onChange: (selectedRowKeys: React.Key[]) => {
				onSelectChange(selectedRowKeys as string[]);
			},

			getCheckboxProps: (record: any) => {
				const isApproved = record.status === 'APPROVED_BY_ACCOUNTS';
				const recordCurrency = record.isUsdEmployee ? 'USD' : 'ZMW';

				const isSameCurrency =
					selectedLoanKeys.length === 0 ||
					recordCurrency === selectedCurrency;

				return {
					disabled: !isApproved || !isSameCurrency,
				};
			},

			// Add custom selection dropdown
			selections: [
				{
					key: 'select-all-zmw',
					text: 'Select All ZMW',
					onSelect: () => {
						const zmwLoans = tableData
							.filter(
								(loan: any) =>
									loan.status === 'APPROVED_BY_ACCOUNTS' &&
									!loan.isUsdEmployee
							)
							.map((loan: any) => loan.id);
						onSelectChange(zmwLoans);
					},
				},
				{
					key: 'select-all-usd',
					text: 'Select All USD',
					onSelect: () => {
						const usdLoans = tableData
							.filter(
								(loan: any) =>
									loan.status === 'APPROVED_BY_ACCOUNTS' &&
									loan.isUsdEmployee
							)
							.map((loan: any) => loan.id);
						onSelectChange(usdLoans);
					},
				},
				{
					key: 'clear-selection',
					text: 'Clear Selection',
					onSelect: () => {
						onSelectChange([]);
					},
				},
			],
		};
	}, [selectedLoanKeys, onSelectChange, selectedCurrency, tableData]);

	const handleLogsOpen = (loanId: string) => {
		// Commented out API call for now
		// fetchPaymentLogs(midMonthId);

		// Just open the modal
		setSelectedLoanId(loanId);
		setIsLogsModalVisible(true);
	};

	const handleErrorModal = () => {
		setIsBankErrorModalVisible(false);
	};

	const handleRedirect = () => {
		handleErrorModal();
		navigate('/settings/configurations/company-setup?tab=bankDetails');
	};
	return (
		<>
			<Table
				dataSource={tableData}
				pagination={{
					total: totalRecords,
					current: currentPage,
					pageSize: pageSize,
					showSizeChanger: false,
					pageSizeOptions: [10, 20, 50, 100, 200],
				}}
				onChange={tableChangeHandler}
				rowKey={(record) => record.id}
				loading={isLoading}
				className="loan-request-table-component"
				scroll={
					tableData.length > 0
						? { x: 1500, y: 'calc(100vh - 325px)' }
						: undefined
				}
				// ADD rowSelection prop
				// rowSelection={rowSelection}
			>
				<Column
					title="Employee Name | Code"
					dataIndex="employeeName"
					key="employeeName"
					filterSearch={true}
					fixed="left"
					className="bg-white"
					width={200}
					render={(text: string, record: any) => (
						<>
							<span>{text}</span> |{' '}
							<span style={{ color: '#584495' }}>
								{record?.employeeCode}
							</span>
						</>
					)}
				/>
				<Column
					title="Requested By"
					dataIndex="requestedBy"
					key="requestedBy"
					className="bg-white"
					width={130}
				/>
				<Column
					title="Requested Date"
					dataIndex="createdAt"
					key="createdAt"
					sorter={true}
					className="bg-white"
					width={160}
					render={(value) => {
						return formatDate(
							value,
							profileData?.companyDateFormat ?? 'DD/MM/YYYY'
						);
					}}
				/>
				<Column
					title="Credited Date"
					dataIndex="effectiveStartDate"
					key="effectiveStartDate"
					sorter={true}
					className="bg-white"
					width={160}
					render={(value) => (
						<div>
							{value
								? formatDate(
										value,
										profileData?.companyDateFormat ??
											'DD/MM/YYYY'
								  )
								: '-'}
						</div>
					)}
				/>
				<Column
					title="Last Installment Date"
					dataIndex="lastPaidDate"
					key="lastPaidDate"
					className="bg-white"
					width={190}
					render={(value) => (
						<div>
							{value
								? formatDate(
										value,
										profileData?.companyDateFormat ??
											'DD/MM/YYYY'
								  )
								: '-'}
						</div>
					)}
				/>
				<Column
					title="Total Amount"
					dataIndex="amount"
					key="amount"
					sorter={true}
					className="bg-white"
					width={150}
					render={(value: string, record: any) => {
						if (record.isUsdEmployee) {
							return formatNumber(
								Number(value),
								data?.CompanyCurrencies,
								true
							);
						} else {
							return formatNumber(
								Number(value),
								data?.CompanyCurrencies
							);
						}
					}}
				/>
				<Column
					title="Outstanding Amount"
					dataIndex="outstandingAmount"
					key="outstandingAmount"
					className="bg-white"
					width={190}
					render={(value: string, record: any) => {
						if (record.isUsdEmployee) {
							return formatNumber(
								Number(value),
								data?.CompanyCurrencies,
								true
							);
						} else {
							return formatNumber(
								Number(value),
								data?.CompanyCurrencies
							);
						}
					}}
				/>
				<Column
					title={
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'start',
							}}
						>
							Installments <br />{' '}
							<span
								style={{
									color: 'gray',
									fontWeight: 300,
									fontSize: '10px',
								}}
							>
								Paid | Remaining
							</span>
						</div>
					}
					dataIndex="installments"
					key="installments"
					className="bg-white"
					width={130}
					render={(_, record: any) => (
						<Space size="middle">
							<span style={{ color: 'green' }}>
								{record.paidInstallments}
							</span>{' '}
							|{' '}
							<span style={{ color: 'red' }}>
								{record.remainingInstallments}
							</span>
						</Space>
					)}
				/>
				<Column
					title="Payment Method"
					dataIndex="paymentMethod"
					key="paymentMethod"
					width={180}
					className="bg-white"
					render={(_, record: any) => {
						if (record.status !== 'LOAN_CREDITED') {
							return '-';
						}

						let method = '-';

						if (record.LoanBankStatus === 'DONE_VIA_CASH') {
							method = 'Cash';
						} else if (record.LoanBankStatus === 'SUCCESS') {
							method = 'Bank';
						}

						return (
							<span style={{ fontWeight: 500 }}>{method}</span>
						);
					}}
				/>
				<Column
					title="Status"
					dataIndex="status"
					key="status"
					className={`${styles['statusColumn']} bg-white`}
					width={250}
					render={(value, record) => {
						let statusText = '';
						let statusClass = '';
						let StatusIcon = null;

						switch (value) {
							case 'PENDING_FOR_APPROVAL':
								statusText = 'Pending for approval';
								statusClass =
									styles['status-pending-for-approval'];
								StatusIcon = <PendingForApproval />;
								break;
							case 'APPROVED_BY_ACCOUNTS':
								statusText = 'Approved by accounts';
								statusClass =
									styles['status-approved-by-accounts'];
								StatusIcon = <ApprovedByAccounts />;
								break;
							case 'REJECTED_BY_ACCOUNTS':
								statusText = 'Rejected by accounts';
								statusClass =
									styles['status-rejected-by-accounts'];
								StatusIcon = <RejectedByAccounts />;
								break;
							case 'LOAN_PROCESSING':
								statusText = 'Loan processing';
								statusClass = styles['status-loan-processing'];
								StatusIcon = <LoanProcessing />;
								break;
							case 'LOAN_CREDITED':
								statusText = 'Loan credited';
								statusClass = styles['status-loan-credited'];
								StatusIcon = <Paid />;
								break;
							default:
								statusText = 'Unknown Status';
								statusClass = styles['status-unknown'];
						}

						return (
							<div className={styles['statusContainer']}>
								<Dropdown
									overlay={statusMenu(record)}
									trigger={['click']}
								>
									<Button
										size="small"
										type="link"
										onClick={(e) => e.preventDefault()}
										className={styles['dropdownTrigger']}
									>
										<DownCircleOutlined />
									</Button>
								</Dropdown>

								<Tag
									bordered={false}
									className={`${styles['statusTag']} ${statusClass}`}
								>
									<span className={styles['statusIcon']}>
										{StatusIcon}
									</span>
									<span>{statusText}</span>
								</Tag>
							</div>
						);
					}}
				/>
				{/* <Column
					title="Initiate Payment"
					dataIndex="initiatePayment"
					key="initiatePayment"
					width={170}
					align="center"
					sorter={false}
					className="bg-white"
					render={(_, record: any) => {
						const isDisabled =
							record.status !== 'APPROVED_BY_ACCOUNTS';
						const isLoanCredited =
							record.status === 'LOAN_CREDITED';
						return (
							<Tooltip
								title={
									isLoanCredited
										? 'Loan is already credited to the employee'
										: isDisabled
										? 'Payment initiation is available once the Loan is approved by accounts only'
										: 'Initiate single payment'
								}
							>
								{!isDisabled ? (
									<div
										onClick={() => {
											handleModalOpen(record.id);
										}}
										style={{
											cursor: 'pointer',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											fontSize: '19px',
											width: '100%',
											height: '100%',
										}}
									>
										<DollarOutlined />
									</div>
								) : (
									<div className="text-gray-400">N/A</div>
								)}
							</Tooltip>
						);
					}}
				/>
				<Column
					title="Logs"
					dataIndex="logs"
					key="logs"
					width={100}
					align="center"
					className="bg-white"
					render={(_, record: any) => (
						<Tooltip
							title={
								record.isLogExists
									? 'View Payment Logs'
									: 'No Payment Logs'
							}
						>
							<Button
								type="text"
								disabled={!record.isLogExists}
								icon={<FileTextOutlined />}
								style={{
									fontSize: '21px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
								onClick={() => handleLogsOpen(record.id)}
							/>
						</Tooltip>
					)}
				/> */}
				<Column
					title="Action"
					key="action"
					className="bg-white"
					width={120}
					fixed="right"
					render={(_: any, record: any) => (
						<Space size="middle">
							{isEdit &&
							record.status === 'PENDING_FOR_APPROVAL' ? (
								<p
									className="cursor-pointer flex align-center justify-center"
									style={{ fontSize: 18 }}
									onClick={() => editDataHandler(record)}
								>
									<EditOutlined />
								</p>
							) : (
								<p className="cursor-not-allowed flex align-center justify-center">
									<EditActionSvg color="#928F8F" />
								</p>
							)}
							{isDelete &&
							record.status === 'PENDING_FOR_APPROVAL' ? (
								<p
									className="cursor-pointer flex align-center justify-center"
									style={{
										fontSize: 18,
										cursor: 'pointer',
									}}
									onClick={() => deleteDataHandler(record.id)}
								>
									<DeleteActionSvg />
								</p>
							) : (
								<p className="cursor-not-allowed flex align-center justify-center">
									<DeleteActionSvg color="#928F8F" />
								</p>
							)}
						</Space>
					)}
				/>
			</Table>
			{isLogsModalVisible && selectedLoanId && (
				<LogsModal
					isOpen={isLogsModalVisible}
					handleCancel={() => setIsLogsModalVisible(false)}
					loanId={selectedLoanId}
					updateMidMonthStatus={(loanId: string, status: string) => {
						updateLoanRequestStatus(loanId, status);
					}}
				/>
			)}
			<Modal
				open={isBankErrorModalVisible}
				onCancel={() => setIsBankErrorModalVisible(false)}
				footer={null}
				centered
				width={520}
				closable={false}
			>
				<div style={{ textAlign: 'center', padding: '10px 5px' }}>
					<div
						style={{
							width: 70,
							height: 70,
							borderRadius: '50%',
							background: '#FFF7E6',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							margin: '0 auto',
						}}
					>
						<ExclamationCircleOutlined
							style={{ fontSize: 38, color: '#faad14' }}
						/>
					</div>

					<h2 style={{ marginTop: 16, fontWeight: 700 }}>
						Bank Details Required
					</h2>

					<p
						style={{
							fontSize: 15,
							color: '#595959',
							marginTop: 8,
							lineHeight: 1.6,
						}}
					>
						To process payments, please add and verify a bank
						account in the Company Setup section.
					</p>

					<div
						style={{
							textAlign: 'left',
							background: '#fafafa',
							padding: '16px 20px',
							borderRadius: 8,
							marginTop: 24,
							border: '1px solid #f0f0f0',
						}}
					>
						<p
							style={{
								fontWeight: 600,
								marginBottom: 10,
								color: '#444',
							}}
						>
							Bank must meet the following requirements:
						</p>

						<ul
							style={{
								paddingLeft: 20,
								lineHeight: 1.6,
								margin: 0,
							}}
						>
							<li>
								Bank Type: <b>First Capital Bank</b>
							</li>
							<li>
								Account status: <b>Verified</b>
							</li>
							<li>
								Currency:{' '}
								<span
									style={{
										background: '#e6f7ff',
										padding: '2px 6px',
										borderRadius: 4,
										fontWeight: 600,
									}}
								>
									ZMW
								</span>
							</li>
							<li>Fund Transfer must be enabled</li>
						</ul>
					</div>

					<Button
						type="primary"
						size="large"
						style={{ marginTop: 26, borderRadius: 6 }}
						onClick={handleRedirect}
					>
						Go to Company Setup
					</Button>
				</div>
			</Modal>
		</>
	);
};

export default LoanRequestTable;
